import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language, toggleLanguage } = useLanguage();
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
            setError(language === 'ar' 
                ? 'رابط إعادة التعيين غير صالح أو مفقود. يرجى طلب إعادة تعيين كلمة مرور جديدة.'
                : 'Invalid or missing reset token. Please request a new password reset.');
        }
    }, [token, language]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords
        if (newPassword.length < 6) {
            setError(language === 'ar' 
                ? 'يجب أن تكون كلمة المرور 6 أحرف على الأقل'
                : 'Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(language === 'ar' 
                ? 'كلمات المرور غير متطابقة'
                : 'Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/v1/helpdesk/client/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    token: token,
                    new_password: newPassword 
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || (language === 'ar' 
                    ? 'فشل في إعادة تعيين كلمة المرور. حاول مرة أخرى.'
                    : 'Failed to reset password. Please try again.'));
                if (data.message && data.message.includes('expired')) {
                    setTokenValid(false);
                }
            }
        } catch (err) {
            setError(language === 'ar' 
                ? 'خطأ في الاتصال. حاول مرة أخرى.'
                : 'Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Success State
    if (success) {
        return (
            <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
                <div className="auth-container">
                    <button onClick={toggleLanguage} className="lang-toggle">
                        🌐 {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                    
                    <div className="auth-logo">
                        <img src="/logo.png" alt="Matix ERP" />
                    </div>
                    
                    <div className="success-icon-wrapper">
                        <span className="success-icon-large">✓</span>
                    </div>
                    
                    <h1>{language === 'ar' ? 'تم بنجاح!' : 'Success!'}</h1>
                    <p className="auth-subtitle">
                        {language === 'ar' 
                            ? 'تم إعادة تعيين كلمة المرور الخاصة بك بنجاح.'
                            : 'Your password has been reset successfully.'}
                    </p>

                    <div className="success-message-box">
                        <p>
                            {language === 'ar' 
                                ? 'يمكنك الآن تسجيل الدخول باستخدام كلمة المرور الجديدة.'
                                : 'You can now log in with your new password.'}
                        </p>
                    </div>

                    <button 
                        className="auth-submit"
                        onClick={() => navigate('/login')}
                    >
                        {language === 'ar' ? '← الذهاب لتسجيل الدخول' : '← Go to Login'}
                    </button>

                    <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
                </div>
            </div>
        );
    }

    // Invalid Token State
    if (!tokenValid) {
        return (
            <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
                <div className="auth-container">
                    <button onClick={toggleLanguage} className="lang-toggle">
                        🌐 {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                    
                    <div className="auth-logo">
                        <img src="/logo.png" alt="Matix ERP" />
                    </div>
                    
                    <div className="error-icon-wrapper">
                        <span className="error-icon-large">✕</span>
                    </div>
                    
                    <h1>{language === 'ar' ? 'رابط غير صالح' : 'Invalid Link'}</h1>
                    <p className="auth-subtitle">
                        {language === 'ar' 
                            ? 'رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية.'
                            : 'This password reset link is invalid or has expired.'}
                    </p>

                    <div className="error-message-box">
                        <p>{error}</p>
                    </div>

                    <Link to="/login" className="auth-submit" style={{ textDecoration: 'none', textAlign: 'center', display: 'block' }}>
                        {language === 'ar' ? '← العودة لتسجيل الدخول' : '← Back to Login'}
                    </Link>

                    <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
                </div>
            </div>
        );
    }

    // Reset Password Form
    return (
        <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <div className="auth-container">
                <button onClick={toggleLanguage} className="lang-toggle">
                    🌐 {language === 'ar' ? 'English' : 'العربية'}
                </button>
                
                <div className="auth-logo">
                    <img src="/logo.png" alt="Matix ERP" />
                </div>
                
                <h1>{language === 'ar' ? 'كلمة مرور جديدة' : 'New Password'}</h1>
                <p className="auth-subtitle">
                    {language === 'ar' 
                        ? 'أدخل كلمة المرور الجديدة أدناه'
                        : 'Enter your new password below'}
                </p>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>{language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}</label>
                        <div className="password-input">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder={language === 'ar' ? 'أدخل كلمة المرور الجديدة (6 أحرف على الأقل)' : 'Enter new password (min 6 characters)'}
                                required
                                minLength={6}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'إظهار' : 'Show')}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}</label>
                        <div className="password-input">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور' : 'Re-enter your password'}
                                required
                                minLength={6}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'إظهار' : 'Show')}
                            </button>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="password-requirements">
                        <p className={newPassword.length >= 6 ? 'requirement-met' : ''}>
                            {newPassword.length >= 6 ? '✓' : '○'} {language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters'}
                        </p>
                        <p className={newPassword && newPassword === confirmPassword ? 'requirement-met' : ''}>
                            {newPassword && newPassword === confirmPassword ? '✓' : '○'} {language === 'ar' ? 'كلمات المرور متطابقة' : 'Passwords match'}
                        </p>
                    </div>

                    <button 
                        type="submit" 
                        className="auth-submit"
                        disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                    >
                        {loading 
                            ? (language === 'ar' ? 'جاري الإعادة...' : 'Resetting...') 
                            : (language === 'ar' ? '🔐 إعادة تعيين كلمة المرور' : '🔐 Reset Password')}
                    </button>
                </form>

                <Link to="/login" className="back-to-login-link">
                    {language === 'ar' ? '← العودة لتسجيل الدخول' : '← Back to Login'}
                </Link>

                <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
            </div>
        </div>
    );
}

export default ResetPassword;
